import type { ContentSession } from '@/src/queries/library-queries'
import React, { useCallback } from 'react'
import { FlatList, Modal, Pressable, Text, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'

import { Icon, symbolName } from '../../../components/ui/Icon'

const NOOP = () => {}

interface StepSelectorProps {
  isVisible: boolean
  onClose: () => void
  steps: ContentSession['steps']
  activeStepIndex: number
  onSelectStep: (stepIndex: number) => void | Promise<void>
}

function StepSelectorItem({
  isActive,
  onClose,
  onSelectStep,
  step,
  stepIndex,
}: {
  isActive: boolean
  onClose: () => void
  onSelectStep: (stepIndex: number) => void | Promise<void>
  step: ContentSession['steps'][number]
  stepIndex: number
}) {
  const handlePress = useCallback(() => {
    onSelectStep(stepIndex)
    onClose()
  }, [onClose, onSelectStep, stepIndex])

  return (
    <Pressable
      className={`py-2 px-3 rounded-lg border flex-row items-center justify-between ${
        isActive ? 'bg-primary border-primary' : 'bg-white border-gray-200'
      }`}
      onPress={handlePress}
    >
      <Text
        className={`text-sm font-medium flex-1 ${
          isActive ? 'text-white' : 'text-gray-900'
        }`}
        numberOfLines={2}
      >
        {stepIndex + 1}. {step.title}
      </Text>
      {isActive && (
        <Icon name={symbolName('checkmark', 'check')} size={16} color="white" />
      )}
    </Pressable>
  )
}

export const StepSelector = ({
  isVisible,
  onClose,
  steps,
  activeStepIndex,
  onSelectStep,
}: StepSelectorProps) => {
  const insets = useSafeAreaInsets()
  const { t } = useTranslation(['session'])
  const renderStep = useCallback(
    ({
      item: step,
      index: stepIndex,
    }: {
      item: ContentSession['steps'][number]
      index: number
    }) => (
      <StepSelectorItem
        isActive={activeStepIndex === stepIndex}
        onClose={onClose}
        onSelectStep={onSelectStep}
        step={step}
        stepIndex={stepIndex}
      />
    ),
    [activeStepIndex, onClose, onSelectStep],
  )

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={isVisible}
      onRequestClose={onClose}
    >
      <Pressable className="flex-1" onPress={onClose}>
        <View className="flex-1 bg-black/20">
          <Pressable
            onPress={NOOP}
            className="absolute bg-white rounded-xl shadow-lg w-[280px]"
            style={{
              top: insets.top + 50,
              left: 20,
            }}
          >
            <Text className="text-base font-semibold text-center py-3 border-b border-gray-100">
              {t('session:steps')}
            </Text>

            <FlatList
              className="max-h-[320px]"
              data={steps}
              keyExtractor={(step) => step.id}
              contentContainerStyle={{ padding: 10, gap: 8 }}
              showsVerticalScrollIndicator={true}
              renderItem={renderStep}
            />
          </Pressable>
        </View>
      </Pressable>
    </Modal>
  )
}
